import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { describeSkipReason, type ReviewChannel, type ReviewRequestStatus } from '@/hooks/useReviewRequests';
import { calculateChange, getComparisonPeriod } from '@/utils/periodComparison';
import type { MetricComparison } from '@/components/reports/MetricCard';

/**
 * Reports → Reviews. Everything is derived from review_requests (+ the
 * Postmark open/click stamps on email_messages), counted as a cohort:
 *
 *   sent, opened, clicked, rated, stars   requests whose sent_at is in range,
 *                                         and what those customers did after
 *   not sent                              requests created in range that were
 *                                         skipped, cancelled or failed
 *   opted out                             customers flagged, all time (there is
 *                                         no opt-out timestamp to range on)
 *
 * Volumes are small (one row per paid invoice), so rows are pulled and
 * summarised in the browser rather than through an RPC.
 */

export interface ReviewReportRow {
  id: string;
  status: ReviewRequestStatus;
  channel: ReviewChannel;
  created_at: string;
  sent_at: string | null;
  skip_reason: string | null;
  error: string | null;
  rating: number | null;
  rated_at: string | null;
  rating_comment: string | null;
  to_email: string | null;
  to_phone: string | null;
  email_message_id: string | null;
  customer_id: string | null;
  job_id: string | null;
  customers: { customer_name: string } | null;
  email_opened_at?: string | null;
  email_clicked_at?: string | null;
}

export interface ReviewSummary {
  sent: number;
  sentEmail: number;
  sentSms: number;
  opened: number;
  clicked: number;
  rated: number;
  averageRating: number | null;
  /** Count per star, index 0 = 1 star … index 4 = 5 stars. */
  stars: [number, number, number, number, number];
  waiting: number;
  notSent: { reason: string; count: number }[];
  notSentTotal: number;
  /** Rated requests, newest rating first. */
  feedback: ReviewReportRow[];
}

export interface ReviewReport {
  current: ReviewSummary;
  optedOut: number;
  comparisons: {
    sent?: MetricComparison;
    rated?: MetricComparison;
    averageRating?: MetricComparison;
  };
}

const COLUMNS =
  'id, status, channel, created_at, sent_at, skip_reason, error, rating, rated_at, rating_comment, to_email, to_phone, email_message_id, customer_id, job_id, customers (customer_name)';

const PAGE = 1000;

async function fetchRows(companyId: string, start: Date, end: Date): Promise<ReviewReportRow[]> {
  const s = start.toISOString();
  const e = end.toISOString();
  const rows: ReviewReportRow[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('review_requests')
      .select(COLUMNS)
      .eq('company_id', companyId)
      .or(`and(sent_at.gte.${s},sent_at.lte.${e}),and(created_at.gte.${s},created_at.lte.${e})`)
      .order('created_at', { ascending: false })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const page = (data ?? []) as unknown as ReviewReportRow[];
    rows.push(...page);
    if (page.length < PAGE) break;
  }

  // Opens and clicks live on the email record; no FK to embed through.
  const ids = rows.map((r) => r.email_message_id).filter((id): id is string => !!id);
  const stamps = new Map<string, { opened_at: string | null; clicked_at: string | null }>();
  for (let i = 0; i < ids.length; i += 200) {
    const { data } = await supabase
      .from('email_messages')
      .select('id, opened_at, clicked_at')
      .in('id', ids.slice(i, i + 200));
    for (const m of data ?? []) stamps.set(m.id, { opened_at: m.opened_at, clicked_at: m.clicked_at });
  }
  for (const r of rows) {
    const m = r.email_message_id ? stamps.get(r.email_message_id) : undefined;
    r.email_opened_at = m?.opened_at ?? null;
    r.email_clicked_at = m?.clicked_at ?? null;
  }
  return rows;
}

function inRange(iso: string | null, start: Date, end: Date): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= start.getTime() && t <= end.getTime();
}

export function summarise(rows: ReviewReportRow[], start: Date, end: Date): ReviewSummary {
  const sentRows = rows.filter((r) => r.status === 'sent' && inRange(r.sent_at, start, end));
  const emailRows = sentRows.filter((r) => !!r.to_email);
  const ratedRows = sentRows.filter((r) => r.rating != null);

  const stars: ReviewSummary['stars'] = [0, 0, 0, 0, 0];
  for (const r of ratedRows) {
    const n = Math.min(5, Math.max(1, Math.round(r.rating as number)));
    stars[n - 1] += 1;
  }
  const averageRating = ratedRows.length
    ? ratedRows.reduce((sum, r) => sum + (r.rating as number), 0) / ratedRows.length
    : null;

  const created = rows.filter((r) => inRange(r.created_at, start, end));
  const reasons = new Map<string, number>();
  for (const r of created) {
    if (r.status !== 'skipped' && r.status !== 'cancelled' && r.status !== 'failed') continue;
    const label = r.status === 'failed' ? 'Failed to send' : describeSkipReason(r.skip_reason) || 'Not sent';
    reasons.set(label, (reasons.get(label) ?? 0) + 1);
  }
  const notSent = [...reasons.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return {
    sent: sentRows.length,
    sentEmail: emailRows.length,
    sentSms: sentRows.filter((r) => !!r.to_phone).length,
    opened: emailRows.filter((r) => !!r.email_opened_at || !!r.email_clicked_at).length,
    clicked: emailRows.filter((r) => !!r.email_clicked_at).length,
    rated: ratedRows.length,
    averageRating,
    stars,
    waiting: created.filter((r) => r.status === 'queued' || r.status === 'sending').length,
    notSent,
    notSentTotal: notSent.reduce((sum, n) => sum + n.count, 0),
    feedback: [...ratedRows].sort((a, b) => (b.rated_at ?? '').localeCompare(a.rated_at ?? '')),
  };
}

function comparison(current: number, previous: number, label: string): MetricComparison {
  const c = calculateChange(current, previous);
  return { percent: c.percent, direction: c.direction, label };
}

export function useReviewReport(startDate: Date, endDate: Date, compareEnabled = false) {
  const { tenantId } = useTenant();

  return useQuery({
    queryKey: ['review-report', tenantId, startDate.toISOString(), endDate.toISOString(), compareEnabled],
    enabled: !!tenantId,
    queryFn: async (): Promise<ReviewReport> => {
      const companyId = tenantId!;
      const prev = getComparisonPeriod(startDate, endDate);

      const [rows, prevRows, opted] = await Promise.all([
        fetchRows(companyId, startDate, endDate),
        compareEnabled ? fetchRows(companyId, prev.start, prev.end) : Promise.resolve(null),
        supabase
          .from('customers')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('review_requests_opted_out', true),
      ]);

      const current = summarise(rows, startDate, endDate);
      const comparisons: ReviewReport['comparisons'] = {};
      if (prevRows) {
        const p = summarise(prevRows, prev.start, prev.end);
        comparisons.sent = comparison(current.sent, p.sent, prev.label);
        comparisons.rated = comparison(current.rated, p.rated, prev.label);
        if (current.averageRating != null && p.averageRating != null) {
          comparisons.averageRating = comparison(current.averageRating, p.averageRating, prev.label);
        }
      }

      return { current, optedOut: opted.count ?? 0, comparisons };
    },
  });
}
